import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { galleryCards } from "@/mocks/gallery";
import ProjectHero from "./components/ProjectHero";
import ChapterBlock from "./components/ChapterBlock";
import TechniqueGrid from "./components/TechniqueGrid";
import ImageGallery from "./components/ImageGallery";
import ProjectCredits from "./components/ProjectCredits";
import RelatedProjects from "./components/RelatedProjects";

export default function ProjectPage() {
  const { slug } = useParams<{ slug: string }>();
  const [scrollY, setScrollY] = useState(0);

  const project = useMemo(
    () => galleryCards.find((p) => p.slug === slug),
    [slug]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0b0b0b] text-white flex flex-col items-center justify-center px-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.45em] text-white/50 mb-6">
          Project not found
        </div>
        <h1 className="font-black uppercase text-4xl md:text-6xl mb-8">
          This story lives elsewhere.
        </h1>
        <Link
          to="/"
          className="text-[11px] uppercase tracking-[0.35em] text-white/80 hover:text-white cursor-pointer whitespace-nowrap border-b border-white/40 pb-1"
        >
          ← Return to the studio
        </Link>
      </div>
    );
  }

  return (
    <div
      className="relative bg-[#0b0b0b] text-white"
      style={{
        fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* Persistent top bar — always visible on story page for easy back navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-14 py-5 bg-black/40 backdrop-blur-md">
        <Link
          to="/"
          className="flex items-center gap-3 group cursor-pointer whitespace-nowrap"
        >
          <span className="w-6 h-6 rounded-full border border-white/50 flex items-center justify-center transition-colors group-hover:border-white">
            <i className="ri-arrow-left-line text-white text-xs" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/80 group-hover:text-white">
            Back to studio
          </span>
        </Link>
        <div className="hidden md:block text-[10px] uppercase tracking-[0.4em] text-white/70">
          {project.title}
        </div>
      </header>

      <ProjectHero project={project} scrollY={scrollY} />

      {/* Intro paragraph */}
      <section className="relative px-6 md:px-14 py-24 md:py-32 max-w-[1000px] mx-auto">
        <p
          className="text-white/80 leading-[1.7] font-light"
          style={{ fontSize: "clamp(18px, 1.3vw, 22px)" }}
        >
          {project.intro}
        </p>
      </section>

      {/* Chapters — full-bleed alternating editorial layout */}
      {project.chapters.map((chapter, idx) => (
        <ChapterBlock key={`${project.slug}-ch-${idx}`} chapter={chapter} index={idx} />
      ))}

      {/* Pull-quote break */}
      <section className="relative px-6 md:px-14 py-28 md:py-40 max-w-[1180px] mx-auto text-center">
        <blockquote
          className="font-light italic leading-[1.15] tracking-tight text-white/90"
          style={{ fontSize: "clamp(28px, 3.6vw, 52px)" }}
        >
          &ldquo;{project.pullquote.quote}&rdquo;
        </blockquote>
        <div className="mt-10 text-[10px] uppercase tracking-[0.45em] text-white/50">
          {project.pullquote.attribution}
        </div>
      </section>

      <TechniqueGrid techniques={project.techniques} />

      <ImageGallery images={project.images} title={project.title} />

      <ProjectCredits credits={project.credits} />

      <RelatedProjects currentSlug={project.slug} />

      {/* Sign-off strip */}
      <footer className="relative px-6 md:px-14 py-12 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[10px] uppercase tracking-[0.35em] text-white/50">
        <span>© 2025 Elin Vahr Studio</span>
        <Link
          to="/"
          className="text-white/80 hover:text-white cursor-pointer whitespace-nowrap"
        >
          ← Back to studio
        </Link>
      </footer>
    </div>
  );
}